import { EmployeeInformationBase } from "../json-schemas/base/employee_information"
import { I18nService } from "../utils/i18n.service"
import { InterventionPlace, offeredServiceI18nInterventionPlace } from "./offered-services.service"
import EmployeeInformationSchema from "../../../../../../json_schemas/base/employee_information.json"
import { SupplierBase } from '../json-schemas/base/supplier'
import { OfferedServiceBase } from "../json-schemas/base/offered_service"
import { OfferedServiceSubscriptionBase } from "../json-schemas/base/offered_service_subscription"
import { EmployeeFieldStandardBase } from "../json-schemas/base/employee_field_standard"
import { MarkerColor } from "../../components/shared/leaflet.component"

export type EmployeeType = EmployeeInformationBase['employee_type'];
export type EmployeeInformationState = EmployeeInformationBase['state'];
export type ContractType = EmployeeInformationBase['contract_type'];
export type InterventionSectorReference = EmployeeInformationBase['intervention_sector_reference'];
export type UnsupportedPets = EmployeeInformationBase['unsupported_pets'][0];

export const employeeInformationEmployeeTypes = EmployeeInformationSchema.properties.employee_type.enum as EmployeeType[]
const i18nScoped = new I18nService(require('./employee-informations.service.yml'))

export function employeeInformationI18nInterventionSectorReference (reference: InterventionSectorReference, distanceRange: number) {
  return i18nScoped.t(`intervention_sector_references.${reference}`, { km: distanceRange })
}

export function employeeInformationI18nTemporaryInterventionSectorReference (reference: InterventionSectorReference, distanceRange: number) {
  return i18nScoped.t(`temporary_intervention_sector_references.${reference}`, { km: distanceRange })
}

export function employeeInformationComputedState (ei: Pick<EmployeeInformationBase, 'unexempted_independant_with_unpaid_or_without_plan?'> & { state: EmployeeInformationState | 'deleted' }) {
  if (ei.state !== 'published' || !ei['unexempted_independant_with_unpaid_or_without_plan?']) return ei.state
  return 'unexempted_independant_with_unpaid_or_without_plan' as const
}

export function employeeInformationI18nState (state: ReturnType<typeof employeeInformationComputedState>) {
  return i18nScoped.t(`state.${state}`)
}

export function employeeInformationStateCssClass (state: ReturnType<typeof employeeInformationComputedState>) {
  return { 
    draft: 'ribbon--alternate', 
    candidate: 'u-txtClr1', 
    deactivated: 'ribbon--alternate', 
    published: 'ribbon--accepted',
    unexempted_independant_with_unpaid_or_without_plan: 'ribbon--alternate',
    deleted: ''
  }[state]
}

export function employeeInformationStateMarkerColor (state: ReturnType<typeof employeeInformationComputedState>): MarkerColor {
  return ({ 
    draft: 'yellow', 
    candidate: 'grey', 
    deactivated: 'orange', 
    published: 'blue',
    unexempted_independant_with_unpaid_or_without_plan: 'yellow',
    deleted: 'red'
  } as const)[state]
}

export function employeeInformationI18nEmployeeType (employeeType: EmployeeType) {
  return i18nScoped.t(`employee_type.${employeeType}`)
}

export function employeeInformationI18nInterventionPlace (interventionPlace: InterventionPlace) {
  return offeredServiceI18nInterventionPlace(interventionPlace)
}

export function employeeInformationI18nUnsupportedPet (unsupportedPet: UnsupportedPets) {
  return i18nScoped.t(`unsupported_pets.${unsupportedPet}`)
}

export function employeeTypeOfDirectPayment (employeeType: EmployeeType) {
  return employeeType === 'independant'
}

export function employeeInformationActiveDirectPayment (
  { employeeInformation, market, offeredService, offeredServiceSubscription } : {
    employeeInformation: Pick<EmployeeInformationBase, 'employee_type' | 'direct_payment'>
    market: Pick<SupplierBase, 'employee_independant_direct_payment?'>
    offeredService?: Pick<OfferedServiceBase, 'no_employee_direct_payment' | 'partial_payment_percentage'>
    offeredServiceSubscription?: Pick<OfferedServiceSubscriptionBase, 'no_employee_direct_payment' | 'partial_payment_percentage'>
  }
) {
  if (!employeeTypeOfDirectPayment(employeeInformation.employee_type)) return false
  if (!employeeInformation.direct_payment)  return false
  if (!market['employee_independant_direct_payment?']) return false
  if (!offeredService && !offeredServiceSubscription) return false

  const osOrOss = offeredServiceSubscription || offeredService
  return !osOrOss.no_employee_direct_payment && osOrOss.partial_payment_percentage === 0
}

export function employeeInformationVisibleEmployeeTypes (
  market: Pick<SupplierBase, 'recruitment?' | 'employee_independant_module?' | 'employee_subcontracting_module?' | 'employee_of_customer_module?'>,
  employeeInformation: Pick<EmployeeInformationBase, 'employee_type'>,
  employeeTypeFieldConfig?: Pick<EmployeeFieldStandardBase, 'extra'>
) {
  let employeeTypes = (market['recruitment?'] && employeeTypeFieldConfig ? employeeTypeFieldConfig.extra.one_of : employeeInformationEmployeeTypes) as EmployeeType[]
  if (!market['employee_independant_module?'] && employeeInformation.employee_type !== 'independant') employeeTypes = employeeTypes.filter(_ => _ !== 'independant')
  if (!market['employee_subcontracting_module?'] && employeeInformation.employee_type !== 'subcontractor') employeeTypes = employeeTypes.filter(_ => _ !== 'subcontractor')
  if (!market['employee_of_customer_module?'] && employeeInformation.employee_type !== 'employee_of_customer') employeeTypes = employeeTypes.filter(_ => _ !== 'employee_of_customer')
  return employeeTypes
}
